import apiClient from './client';
import type {
  AnalyticsBucketGranularity,
  AnalyticsTimeseries,
  ChatMessage,
  ChatMessageCreate,
  FeedEvent,
  FeedEventCreate,
  Feedback,
  FeedbackCreate,
  FeedbackSummary,
  PaginatedList,
  Run,
  RunAnalytics,
  RunAnalyticsExportTable,
  RunChallenge,
  RunCreate,
  RunSchedule,
  RunStation,
  RunUpdate,
  Score,
  ScoreCreate,
  ScoreList,
  ScoreUpdate,
  Scoreboard,
  Submission,
  SubmissionCreate,
  SubmissionUpdate,
  Team,
  TeamCreate,
  TeamProgressResponse,
} from './types';

// ============================================================================
// Runs
// ============================================================================

export async function listRuns(orgSlug: string): Promise<PaginatedList<Run>> {
  const { data } = await apiClient.get<PaginatedList<Run>>(`/api/orgs/${orgSlug}/runs/`);
  return data;
}

export async function getRun(orgSlug: string, runId: string): Promise<Run> {
  const { data } = await apiClient.get<Run>(`/api/orgs/${orgSlug}/runs/${runId}`);
  return data;
}

export async function createRun(orgSlug: string, body: RunCreate): Promise<Run> {
  const { data } = await apiClient.post<Run>(`/api/orgs/${orgSlug}/runs/`, body);
  return data;
}

export async function updateRun(orgSlug: string, runId: string, body: RunUpdate): Promise<Run> {
  const { data } = await apiClient.put<Run>(`/api/orgs/${orgSlug}/runs/${runId}`, body);
  return data;
}

export async function deleteRun(orgSlug: string, runId: string): Promise<void> {
  await apiClient.delete(`/api/orgs/${orgSlug}/runs/${runId}`);
}

// ============================================================================
// Run state transitions
// ============================================================================

export async function startRun(orgSlug: string, runId: string): Promise<Run> {
  const { data } = await apiClient.post<Run>(`/api/orgs/${orgSlug}/runs/${runId}/start`);
  return data;
}

export async function scheduleRun(
  orgSlug: string,
  runId: string,
  body: RunSchedule,
): Promise<Run> {
  const { data } = await apiClient.post<Run>(`/api/orgs/${orgSlug}/runs/${runId}/schedule`, body);
  return data;
}

export async function pauseRun(orgSlug: string, runId: string): Promise<Run> {
  const { data } = await apiClient.post<Run>(`/api/orgs/${orgSlug}/runs/${runId}/pause`);
  return data;
}

export async function resumeRun(orgSlug: string, runId: string): Promise<Run> {
  const { data } = await apiClient.post<Run>(`/api/orgs/${orgSlug}/runs/${runId}/resume`);
  return data;
}

export async function endRun(orgSlug: string, runId: string): Promise<Run> {
  const { data } = await apiClient.post<Run>(`/api/orgs/${orgSlug}/runs/${runId}/end`);
  return data;
}

// ============================================================================
// Frozen snapshots
// ============================================================================

export async function listRunStations(
  orgSlug: string,
  runId: string,
): Promise<PaginatedList<RunStation>> {
  const { data } = await apiClient.get<PaginatedList<RunStation>>(
    `/api/orgs/${orgSlug}/runs/${runId}/stations/`,
  );
  return data;
}

export async function listRunChallenges(
  orgSlug: string,
  runId: string,
  runStationId: string,
): Promise<PaginatedList<RunChallenge>> {
  const { data } = await apiClient.get<PaginatedList<RunChallenge>>(
    `/api/orgs/${orgSlug}/runs/${runId}/stations/${runStationId}/challenges/`,
  );
  return data;
}

// ============================================================================
// Teams
// ============================================================================

export async function listTeams(orgSlug: string, runId: string): Promise<PaginatedList<Team>> {
  const { data } = await apiClient.get<PaginatedList<Team>>(
    `/api/orgs/${orgSlug}/runs/${runId}/teams/`,
  );
  return data;
}

export async function getTeam(orgSlug: string, runId: string, teamId: string): Promise<Team> {
  const { data } = await apiClient.get<Team>(
    `/api/orgs/${orgSlug}/runs/${runId}/teams/${teamId}`,
  );
  return data;
}

/** Spec 023 — registered participant joins a team via team-code. */
export async function joinTeamByCode(
  orgSlug: string,
  runId: string,
  teamCode: string,
): Promise<void> {
  await apiClient.post(`/api/orgs/${orgSlug}/runs/${runId}/teams/by-code`, { team_code: teamCode });
}

export async function createTeam(
  orgSlug: string,
  runId: string,
  body: TeamCreate,
): Promise<Team> {
  const { data } = await apiClient.post<Team>(
    `/api/orgs/${orgSlug}/runs/${runId}/teams/`,
    body,
  );
  return data;
}

export async function joinTeam(
  orgSlug: string,
  runId: string,
  teamId: string,
): Promise<void> {
  await apiClient.post(`/api/orgs/${orgSlug}/runs/${runId}/teams/${teamId}/join`);
}

export async function leaveTeam(
  orgSlug: string,
  runId: string,
  teamId: string,
): Promise<void> {
  await apiClient.post(`/api/orgs/${orgSlug}/runs/${runId}/teams/${teamId}/leave`);
}

// memberId is the TeamMember UUID from TeamResponse.members, not a user id.
export async function removeTeamMember(
  orgSlug: string,
  runId: string,
  teamId: string,
  memberId: string,
): Promise<void> {
  await apiClient.delete(
    `/api/orgs/${orgSlug}/runs/${runId}/teams/${teamId}/members/${memberId}`,
  );
}

// ============================================================================
// Submissions
// ============================================================================

export async function listSubmissions(
  orgSlug: string,
  runId: string,
): Promise<PaginatedList<Submission>> {
  const { data } = await apiClient.get<PaginatedList<Submission>>(
    `/api/orgs/${orgSlug}/runs/${runId}/submissions/`,
  );
  return data;
}

export async function getSubmission(
  orgSlug: string,
  runId: string,
  submissionId: string,
): Promise<Submission> {
  const { data } = await apiClient.get<Submission>(
    `/api/orgs/${orgSlug}/runs/${runId}/submissions/${submissionId}`,
  );
  return data;
}

export async function createSubmission(
  orgSlug: string,
  runId: string,
  body: SubmissionCreate,
): Promise<Submission> {
  const { data } = await apiClient.post<Submission>(
    `/api/orgs/${orgSlug}/runs/${runId}/submissions/`,
    body,
  );
  return data;
}

/** Replace an unscored submission's content (issue #255). */
export async function updateSubmission(
  orgSlug: string,
  runId: string,
  submissionId: string,
  body: SubmissionUpdate,
): Promise<Submission> {
  const { data } = await apiClient.put<Submission>(
    `/api/orgs/${orgSlug}/runs/${runId}/submissions/${submissionId}`,
    body,
  );
  return data;
}

// ============================================================================
// Scores
// ============================================================================

export async function listScores(orgSlug: string, runId: string): Promise<ScoreList> {
  const { data } = await apiClient.get<ScoreList>(`/api/orgs/${orgSlug}/runs/${runId}/scores/`);
  return data;
}

export async function createScore(
  orgSlug: string,
  runId: string,
  body: ScoreCreate,
): Promise<Score> {
  const { data } = await apiClient.post<Score>(
    `/api/orgs/${orgSlug}/runs/${runId}/scores/`,
    body,
  );
  return data;
}

export async function updateScore(
  orgSlug: string,
  runId: string,
  scoreId: string,
  body: ScoreUpdate,
): Promise<Score> {
  const { data } = await apiClient.put<Score>(
    `/api/orgs/${orgSlug}/runs/${runId}/scores/${scoreId}`,
    body,
  );
  return data;
}

/** Re-open a graded submission by deleting its score (issue #255). */
export async function deleteScore(
  orgSlug: string,
  runId: string,
  scoreId: string,
): Promise<void> {
  await apiClient.delete(`/api/orgs/${orgSlug}/runs/${runId}/scores/${scoreId}`);
}

export async function getScoreboard(orgSlug: string, runId: string): Promise<Scoreboard> {
  const { data } = await apiClient.get<Scoreboard>(
    `/api/orgs/${orgSlug}/runs/${runId}/scores/scoreboard`,
  );
  return data;
}

export async function getTeamProgress(
  orgSlug: string,
  runId: string,
): Promise<TeamProgressResponse> {
  const { data } = await apiClient.get<TeamProgressResponse>(
    `/api/orgs/${orgSlug}/runs/${runId}/team-progress`,
  );
  return data;
}

// ============================================================================
// Feed
// ============================================================================

// `latest` asks for the newest window rather than the first page. Both a feed
// and a chat are read at their end, so without it a long-running run opens on
// its own beginning and never shows what just happened.
export async function listFeed(
  orgSlug: string,
  runId: string,
): Promise<PaginatedList<FeedEvent>> {
  const { data } = await apiClient.get<PaginatedList<FeedEvent>>(
    `/api/orgs/${orgSlug}/runs/${runId}/feed/`,
    { params: { latest: true } },
  );
  return data;
}

export async function createFeedEvent(
  orgSlug: string,
  runId: string,
  body: FeedEventCreate,
): Promise<FeedEvent> {
  const { data } = await apiClient.post<FeedEvent>(
    `/api/orgs/${orgSlug}/runs/${runId}/feed/`,
    body,
  );
  return data;
}

// ============================================================================
// Chat
// ============================================================================

export async function listChatMessages(
  orgSlug: string,
  runId: string,
  teamId: string,
): Promise<PaginatedList<ChatMessage>> {
  const { data } = await apiClient.get<PaginatedList<ChatMessage>>(
    `/api/orgs/${orgSlug}/runs/${runId}/teams/${teamId}/chat/`,
    { params: { latest: true } },
  );
  return data;
}

export async function postChatMessage(
  orgSlug: string,
  runId: string,
  teamId: string,
  body: ChatMessageCreate,
): Promise<ChatMessage> {
  const { data } = await apiClient.post<ChatMessage>(
    `/api/orgs/${orgSlug}/runs/${runId}/teams/${teamId}/chat/`,
    body,
  );
  return data;
}

// ============================================================================
// Feedback
// ============================================================================

export async function createFeedback(
  orgSlug: string,
  runId: string,
  body: FeedbackCreate,
): Promise<Feedback> {
  const { data } = await apiClient.post<Feedback>(
    `/api/orgs/${orgSlug}/runs/${runId}/feedback/`,
    body,
  );
  return data;
}

export async function listFeedback(
  orgSlug: string,
  runId: string,
): Promise<PaginatedList<Feedback>> {
  const { data } = await apiClient.get<PaginatedList<Feedback>>(
    `/api/orgs/${orgSlug}/runs/${runId}/feedback/`,
  );
  return data;
}

export async function getFeedbackSummary(
  orgSlug: string,
  runId: string,
): Promise<FeedbackSummary> {
  const { data } = await apiClient.get<FeedbackSummary>(
    `/api/orgs/${orgSlug}/runs/${runId}/feedback/summary`,
  );
  return data;
}

// ============================================================================
// Analytics (spec 038 / #244)
// ============================================================================

export async function getRunAnalytics(orgSlug: string, runId: string): Promise<RunAnalytics> {
  const { data } = await apiClient.get<RunAnalytics>(
    `/api/orgs/${orgSlug}/runs/${runId}/analytics`,
  );
  return data;
}

/**
 * Fetches the formula-injection-safe CSV text for one analytics table
 * (FR-002). Mirrors `exportLogsCsv`'s responseType:'text' pattern — the
 * caller wraps the string in a Blob and triggers the browser download.
 */
export async function exportRunAnalyticsCsv(
  orgSlug: string,
  runId: string,
  table: RunAnalyticsExportTable,
): Promise<string> {
  const { data } = await apiClient.get<string>(
    `/api/orgs/${orgSlug}/runs/${runId}/analytics/export`,
    { params: { table }, responseType: 'text' },
  );
  return data;
}

// ============================================================================
// Date-range analytics — org-scoped timeseries (spec 039 / #405)
// ============================================================================

export interface AnalyticsTimeseriesParams {
  dateFrom: string;
  dateTo: string;
  bucket: AnalyticsBucketGranularity;
}

export async function getAnalyticsTimeseries(
  orgSlug: string,
  { dateFrom, dateTo, bucket }: AnalyticsTimeseriesParams,
): Promise<AnalyticsTimeseries> {
  const { data } = await apiClient.get<AnalyticsTimeseries>(
    `/api/orgs/${orgSlug}/analytics/timeseries`,
    { params: { date_from: dateFrom, date_to: dateTo, bucket } },
  );
  return data;
}

export async function exportAnalyticsTimeseriesCsv(
  orgSlug: string,
  { dateFrom, dateTo, bucket }: AnalyticsTimeseriesParams,
): Promise<string> {
  const { data } = await apiClient.get<string>(
    `/api/orgs/${orgSlug}/analytics/timeseries/export`,
    { params: { date_from: dateFrom, date_to: dateTo, bucket }, responseType: 'text' },
  );
  return data;
}
